import type { NavItem } from '@agentic-flow-studio/shared/types/command-palette';
import {
  Activity,
  BarChart3,
  Calendar,
  ClipboardCheck,
  IdCard,
  Layers,
  LayoutDashboard,
  Settings,
  Stethoscope,
  UserCircle,
  Users,
  Zap,
} from 'lucide-react';

/**
 * Every navigable destination in Inbox Clinic, in the order the sidebar renders them.
 *
 * Single source of truth for both `AppSidebar` and the Ctrl/Cmd+K command palette — adding a
 * route here lights it up in both places at once. Order matters: the palette promotes the
 * leading entries to its root view, so keep the day-to-day surfaces first.
 */
export function buildNavItems(orgId: string, workspaceId: string): NavItem[] {
  const base = `/org/${orgId}/workspace/${workspaceId}`;
  const settingsBase = `${base}/settings`;

  return [
    {
      id: 'dashboard',
      title: 'Action Center',
      icon: LayoutDashboard,
      url: `${base}/dashboard`,
      section: 'Workspace',
      keywords: ['home', 'overview', 'action center', 'loops'],
    },
    {
      id: 'triage',
      title: 'Triage',
      icon: Activity,
      url: `${base}/triage`,
      section: 'Workspace',
      keywords: ['inbox', 'email', 'mail', 'threads', 'harbor'],
      // Lit for any thread under /triage, but not for the settings pages.
      isActive: (pathname) =>
        pathname.startsWith(`${base}/triage`) && !pathname.startsWith(settingsBase),
    },
    {
      id: 'tasks',
      title: 'Tasks',
      icon: ClipboardCheck,
      url: `${base}/tasks`,
      section: 'Workspace',
      keywords: ['prescriptions', 'todo'],
      isActive: (pathname) => pathname.startsWith(`${base}/tasks`),
    },
    {
      id: 'contacts',
      title: 'Contacts',
      icon: Users,
      url: `${base}/contacts`,
      section: 'Workspace',
      keywords: ['people', 'customers', 'anchors'],
      isActive: (pathname) => pathname.startsWith(`${base}/contacts`),
    },
    {
      id: 'users',
      title: 'Users',
      icon: IdCard,
      url: `${base}/users`,
      section: 'Workspace',
      keywords: ['team', 'members', 'staff', 'colleagues', 'directory'],
      isActive: (pathname) => pathname.startsWith(`${base}/users`),
    },
    {
      id: 'calendar',
      title: 'Calendar',
      icon: Calendar,
      url: `${base}/calendar`,
      section: 'Workspace',
      keywords: ['schedule', 'events'],
    },
    {
      id: 'analytics',
      title: 'Analytics',
      icon: BarChart3,
      url: `${base}/analytics`,
      section: 'Workspace',
      keywords: ['reports', 'stats', 'metrics'],
    },
    {
      id: 'clinic-settings',
      title: 'Clinic Settings',
      icon: Stethoscope,
      url: `${settingsBase}/clinic`,
      section: 'Settings',
      keywords: ['accounts', 'gmail', 'connect'],
      isActive: (pathname) => pathname.startsWith(`${settingsBase}/clinic`),
    },
    {
      id: 'automation-rules',
      title: 'Automation Rules',
      icon: Zap,
      url: `${settingsBase}/rules`,
      section: 'Settings',
      keywords: ['automation', 'filters', 'auto reply'],
      isActive: (pathname) => pathname.startsWith(`${settingsBase}/rules`),
    },
    {
      id: 'contact-types',
      title: 'Contact Types',
      icon: Layers,
      url: `${settingsBase}/contact-types`,
      section: 'Settings',
      keywords: ['custom fields', 'schema'],
      isActive: (pathname) => pathname.startsWith(`${settingsBase}/contact-types`),
    },
    {
      // The sidebar swaps this icon for the signed-in user's avatar; the palette uses it as-is.
      id: 'profile',
      title: 'Profile',
      icon: UserCircle,
      url: `${base}/profile`,
      section: 'Account',
      keywords: ['me', 'my account', 'avatar'],
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      url: `${base}/settings`,
      section: 'Account',
      keywords: ['preferences', 'workspace', 'members', 'billing'],
    },
  ];
}
